/**
 * One capability probe, in its own module, for one reason: CHUNK BOUNDARIES.
 *
 * `views.ts` needs to know whether this browser has WebHID so it can label the Ledger path as
 * available or explain why it is not. It used to import that answer from `ledger.ts` — and a
 * static import from the entry graph cancels a dynamic one. Rollup says so out loud:
 *
 *   ledger.ts is dynamically imported by main.ts but also statically imported by views.ts,
 *   dynamic import will not move module into another chunk
 *
 * The heavy stack (`ledger-bitcoin`, `@ledgerhq/hw-transport-webhid`) survived that, because
 * `connect()` imports it dynamically from inside `ledger.ts`. `ledger.ts` itself did not: it was
 * welded into the entry chunk every user downloads, including the ones on Safari, Firefox and
 * iOS for whom WebHID does not exist at all.
 *
 * So the probe lives here, depends on nothing, and both sides import it. Do NOT move the
 * DEFINITION back into `ledger.ts` — that is the one-line change that silently welds the module
 * into the entry chunk again, and it is a change a reviewer would wave through as tidying up.
 * (Measured 2026-08-26: a bare `export { hasWebHid } from './webhid.js'` in `ledger.ts` does not
 * break the split — Rollup resolves the re-export straight through — but it earns nothing and
 * invites the definition to follow it home.) `test/no-network.test.ts` asserts the entry chunk
 * carries no Ledger-only symbol, and that guard was verified to fail on the original bug.
 */

/** WebHID is Chromium-only. Firefox and WebKit declined to implement it, deliberately. */
export const hasWebHid = (): boolean =>
  typeof navigator !== 'undefined' && 'hid' in navigator;
